import { useState, useEffect } from 'react';
import { BRAND, FONTS } from '../styles/tokens';
import { Reveal, SectionLabel } from './ui/Reveal';
import { TESTIMONIALS, FAQS } from '../data/content';
import { useIsMobile } from '../hooks/useIsMobile';

const AUDIENCES = ['Law Enforcement', 'Legal Professionals', 'Healthcare Workers', 'Community Organizations', 'Schools & Child Welfare', 'Advocates & Survivors'];

export function About() {
  const [hovered, setHovered] = useState<number | null>(null);
  const m = useIsMobile();
  return (
    <section id="about" style={{ background: BRAND.cream, padding: m ? '64px 20px' : '100px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr', gap: m ? 36 : 56, alignItems: 'center' }}>
        <Reveal>
          <div style={{ background: BRAND.white, borderRadius: 16, padding: m ? 28 : 44, boxShadow: '0 4px 24px rgba(0,0,0,0.05)', border: `1px solid ${BRAND.border}`, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${BRAND.warm}, ${BRAND.warmDark})` }} />
            <div style={{ fontFamily: FONTS.heading, fontSize: m ? 26 : 30, fontWeight: 700, color: BRAND.navy, marginBottom: 6 }}>Tracy Curtis</div>
            <div style={{ fontFamily: FONTS.body, fontSize: m ? 13 : 14, color: BRAND.warm, fontWeight: 600, letterSpacing: '0.04em', marginBottom: m ? 20 : 28 }}>M.A. Forensic Psychology · CDVP</div>
            {[
              { l: 'Education', v: 'The Chicago School of Professional Psychology' },
              { l: 'Certifications', v: 'CDVP · Mental Health First Aid · ASIST' },
              { l: 'Specialization', v: 'Coercive Control & Pattern-Based Abuse' },
              { l: 'Founded', v: 'EmpowerED: From Awareness to Accountability' },
            ].map((item, i, a) => (
              <div key={i} style={{ borderBottom: i < a.length - 1 ? `1px solid ${BRAND.borderLight}` : 'none', paddingBottom: i < a.length - 1 ? 14 : 0, marginBottom: i < a.length - 1 ? 14 : 0 }}>
                <div style={{ fontFamily: FONTS.body, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: BRAND.textLight, marginBottom: 4 }}>{item.l}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: m ? 13 : 14, color: BRAND.text, fontWeight: 500 }}>{item.v}</div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <SectionLabel>About Tracy</SectionLabel>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 38, fontWeight: 700, color: BRAND.navy, lineHeight: 1.18, marginBottom: 20 }}>Expertise rooted in knowledge and lived experience.</h2>
          <p style={{ fontFamily: FONTS.body, fontSize: m ? 17 : 19, lineHeight: 1.7, color: BRAND.navy, fontWeight: 600, marginBottom: 16 }}>Awareness is not enough.</p>
          <p style={{ fontFamily: FONTS.body, fontSize: m ? 15 : 16, lineHeight: 1.8, color: BRAND.textMid, marginBottom: 24 }}>Most people know domestic abuse exists. What is missing is the action from the awareness that leads to accountability.</p>
          <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: BRAND.textLight, marginBottom: 12 }}>I work with</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {AUDIENCES.map((a, i) => (
              <span key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
                style={{ fontFamily: FONTS.body, fontSize: 12, padding: '8px 14px', borderRadius: 24, background: hovered === i ? BRAND.warm : `${BRAND.warm}12`, color: hovered === i ? BRAND.white : BRAND.warmDark, fontWeight: 500, cursor: 'default', transition: 'all 0.25s' }}>
                {a}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const PILLARS = [
  { n: '01', t: 'Patterns Over Incidents', d: 'Shifting documentation from isolated events to the cumulative pattern of behavior that defines abuse.' },
  { n: '02', t: 'Recognition & Documentation', d: 'Equipping professionals to identify coercive control and document it in ways that hold up in court.' },
  { n: '03', t: 'System Accountability', d: 'Strengthening institutional responses through education that challenges minimization and closes gaps.' },
  { n: '04', t: 'Survivor-Centered Practice', d: 'Centering the lived experience of survivors in every training, policy, and organizational consultation.' },
];

export function Approach() {
  const [hovered, setHovered] = useState<number | null>(null);
  const m = useIsMobile();
  return (
    <section id="approach" style={{ background: BRAND.cream, padding: m ? '64px 20px' : '100px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal>
          <SectionLabel>The EmpowerED Approach</SectionLabel>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 40, fontWeight: 700, color: BRAND.navy, lineHeight: 1.18, marginBottom: 16, maxWidth: 560 }}>Four pillars that move systems from awareness to action.</h2>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(4, 1fr)', gap: m ? 16 : 24, marginTop: m ? 32 : 52 }}>
          {PILLARS.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
                style={{ padding: m ? 24 : 28, borderRadius: 12, background: hovered === i ? BRAND.white : 'transparent', boxShadow: hovered === i ? '0 8px 32px rgba(0,0,0,0.06)' : 'none', border: `1px solid ${hovered === i ? BRAND.border : m ? BRAND.borderLight : 'transparent'}`, transition: 'all 0.3s' }}>
                <div style={{ fontFamily: FONTS.heading, fontSize: m ? 36 : 52, fontWeight: 700, color: `${BRAND.warm}${hovered === i ? '40' : '22'}`, lineHeight: 1 }}>{p.n}</div>
                <h3 style={{ fontFamily: FONTS.heading, fontSize: m ? 18 : 20, fontWeight: 700, color: BRAND.navy, marginBottom: 10, marginTop: 4 }}>{p.t}</h3>
                <p style={{ fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.75, color: BRAND.textMid }}>{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const m = useIsMobile();
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive(a => (a + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <section id="testimonials" style={{ background: BRAND.navy, padding: m ? '64px 20px' : '80px 24px' }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
        <Reveal>
          <SectionLabel center>What Professionals Say</SectionLabel>
          <div style={{ position: 'relative', minHeight: m ? 220 : 200 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{ position: i === active ? 'relative' : 'absolute', top: 0, left: 0, right: 0, opacity: i === active ? 1 : 0, transform: i === active ? 'translateY(0)' : 'translateY(16px)', transition: 'all 0.5s ease', pointerEvents: i === active ? 'auto' : 'none' }}>
                <div style={{ fontFamily: FONTS.heading, fontSize: m ? 20 : 26, fontWeight: 400, fontStyle: 'italic', color: BRAND.white, lineHeight: 1.5, marginBottom: 24 }}>"{t.quote}"</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 600, color: BRAND.warm }}>{t.role}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>{t.org}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 28 }}>
            {TESTIMONIALS.map((_, i) => (
              <button key={i} onClick={() => setActive(i)} style={{ width: i === active ? 32 : 10, height: 10, borderRadius: 5, background: i === active ? BRAND.warm : 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', transition: 'all 0.3s', padding: 0 }} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function FAQ() {
  const [open, setOpen] = useState<number | null>(null);
  const m = useIsMobile();
  return (
    <section id="faq" style={{ background: BRAND.white, padding: m ? '64px 20px' : '100px 24px' }}>
      <div style={{ maxWidth: 780, margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: m ? 32 : 48 }}>
            <SectionLabel center>Frequently Asked Questions</SectionLabel>
            <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 38, fontWeight: 700, color: BRAND.navy }}>Common questions answered.</h2>
          </div>
        </Reveal>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={i} delay={i * 0.04}>
                <div style={{ border: `1px solid ${isOpen ? BRAND.warm + '40' : BRAND.borderLight}`, borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.3s' }}>
                  <button onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: m ? '16px 18px' : '20px 24px', background: isOpen ? BRAND.warmGlow : 'transparent', border: 'none', cursor: 'pointer', transition: 'background 0.3s', textAlign: 'left' }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: m ? 14 : 15, fontWeight: 600, color: BRAND.navy, paddingRight: 16 }}>{f.q}</span>
                    <span style={{ color: BRAND.warm, fontSize: 20, transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.3s', flexShrink: 0 }}>+</span>
                  </button>
                  <div style={{ maxHeight: isOpen ? 400 : 0, overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
                    <p style={{ padding: m ? '0 18px 16px' : '0 24px 20px', fontFamily: FONTS.body, fontSize: 14, lineHeight: 1.8, color: BRAND.textMid, margin: 0 }}>{f.a}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const m = useIsMobile();
  return (
    <section id="contact" style={{ background: BRAND.navy, padding: m ? '48px 20px 32px' : '72px 24px 40px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '1.2fr 1fr 1fr', gap: m ? 32 : 48 }}>
          <div>
            <span style={{ fontFamily: FONTS.heading, fontSize: 24, fontWeight: 700, color: BRAND.warm }}>EmpowerED</span>
            <p style={{ fontFamily: FONTS.body, fontSize: 14, color: 'rgba(255,255,255,0.4)', lineHeight: 1.75, marginTop: 14 }}>From Awareness to Accountability.<br />Domestic abuse education, consultation, and systems change.</p>
          </div>
          <div>
            <h4 style={{ fontFamily: FONTS.body, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND.warm, marginBottom: 14 }}>Connect</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <a href="mailto:curtisempowered@gmail.com" style={{ fontFamily: FONTS.body, fontSize: 14, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>curtisempowered@gmail.com</a>
              <a href="https://www.linkedin.com/in/tracycurtismacdvp" target="_blank" rel="noopener noreferrer" style={{ fontFamily: FONTS.body, fontSize: 14, color: 'rgba(255,255,255,0.55)', textDecoration: 'none' }}>LinkedIn — Tracy Curtis</a>
            </div>
          </div>
          <div>
            <h4 style={{ fontFamily: FONTS.body, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND.warm, marginBottom: 14 }}>Services</h4>
            {[
              { s: 'Speaking Engagements', href: '#speaking' },
              { s: 'Speaker Kit', href: '#speaker-kit' },
              { s: 'Organizational Training', href: '#training' },
              { s: 'Policy Consultation', href: '#lead' },
              { s: 'Case Review Support', href: '#lead' },
            ].map(({ s, href }) => (
              <a key={s} href={href} style={{ display: 'block', fontFamily: FONTS.body, fontSize: 14, color: 'rgba(255,255,255,0.45)', marginBottom: 10, textDecoration: 'none' }}>{s}</a>
            ))}
          </div>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: m ? 32 : 48, paddingTop: 20, display: 'flex', flexDirection: m ? 'column' : 'row', gap: m ? 6 : 0, justifyContent: 'space-between', alignItems: m ? 'center' : undefined }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>© 2026 EmpowerED. All rights reserved.</span>
          <span style={{ fontFamily: FONTS.body, fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>Tracy Curtis, M.A., CDVP</span>
        </div>
      </div>
    </section>
  );
}
